import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Toast from '../Common/Toast';
import { formatPhone, normalizePhoneDigits } from '../../utils/phoneMask';
import { formatLocalDate } from '../../utils/dateTime';
import './UserProfile.css';

const genderOptions = [
  { value: 'not_specified', label: 'Не указан' },
  { value: 'male', label: 'Мужской' },
  { value: 'female', label: 'Женский' },
];

const ROLE_LABELS = {
  user: 'Пользователь',
  admin: 'Администратор',
};

const composeFullName = (lastName, firstName, patronymic) =>
  [lastName, firstName, patronymic]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' ');

const trimDate = (value) => {
  if (!value) {
    return '';
  }
  return formatLocalDate(value);
};

const formatBirthDateInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);
  if (!mm) return dd;
  if (!yyyy) return `${dd}.${mm}`;
  return `${dd}.${mm}.${yyyy}`;
};

const parseBirthDateStrict = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const match = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) return null;
  if (year < 1900 || year > 2100) return null;
  const d = new Date(year, month - 1, day);
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return null;
  }
  return d;
};


const normalizeProfile = (data = {}) => {
  const phoneDigits = normalizePhoneDigits(data.phone || '');
  const fallbackFullName = composeFullName(data.last_name, data.first_name, data.patronymic);
  return {
    last_name: data.last_name || '',
    first_name: data.first_name || '',
    patronymic: data.patronymic || '',
    email: data.email || '',
    phone: phoneDigits ? formatPhone(phoneDigits) : '',
    gender: data.gender || 'not_specified',
    date_of_birth: trimDate(data.date_of_birth || ''),
    inactivity_timeout_minutes: String(data.inactivity_timeout_minutes ?? 30),
    role: data.role || '',
    registration_date: data.registration_date || '',
    full_name: data.full_name || fallbackFullName,
  };
};

const initialProfile = normalizeProfile();

const initialPasswords = {
  current: '',
  next: '',
  confirm: '',
};

const normalizeEmailSettings = (data = {}) => ({
  frontend_url: data.frontend_url || '',
  host: data.host || '',
  port: String(data.port ?? 25),
  host_user: data.host_user || '',
  host_password: '',
  use_tls: Boolean(data.use_tls),
  use_ssl: Boolean(data.use_ssl),
  default_from_email: data.default_from_email || '',
  has_password: Boolean(data.has_password),
});

const initialEmailSettings = normalizeEmailSettings();
const initialDadataSettings = {
  dadata_api_token: '',
  has_dadata_api_token: false,
  telegram_bot_token: '',
  has_telegram_bot_token: false,
  telegram_admin_chat_ids: '',
  telegram_proxy_url: '',
  has_telegram_proxy_url: false,
};

const extractErrorMessage = (rawError) => {
  if (!rawError) {
    return 'Не удалось выполнить действие. Попробуйте позже.';
  }
  if (typeof rawError === 'string') {
    return rawError;
  }
  if (Array.isArray(rawError)) {
    return rawError.map(extractErrorMessage).join(' ');
  }
  if (typeof rawError === 'object') {
    const firstKey = Object.keys(rawError)[0];
    if (firstKey) {
      return extractErrorMessage(rawError[firstKey]);
    }
  }
  return 'Произошла неизвестная ошибка. Попробуйте позже.';
};

const PROFILE_FIELD_LABELS = {
  full_name: 'ФИО',
  email: 'Email',
  phone: 'Телефон',
  date_of_birth: 'Дата рождения',
  inactivity_timeout_minutes: 'Таймаут бездействия',
};

const mapApiFieldErrors = (data) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return {};
  }
  const result = {};
  Object.entries(data).forEach(([key, value]) => {
    if (value == null) return;
    const text = Array.isArray(value) ? value.map((item) => String(item)).join(' ') : String(value);
    if (text.trim()) {
      result[key] = text.trim();
    }
  });
  return result;
};

const UserProfile = () => {
  const { updateUser, user } = useAuth();
  const isAdmin = !!user?.is_staff || !!user?.is_superuser || String(user?.role || '').toLowerCase() === 'admin';
  const [profile, setProfile] = useState(initialProfile);
  const [passwords, setPasswords] = useState(initialPasswords);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [toast, setToast] = useState(null);
  const savedProfileRef = useRef(initialProfile);
  const [adminTestEmail, setAdminTestEmail] = useState('');
  const [adminEmailTesting, setAdminEmailTesting] = useState(false);
  const [emailSettings, setEmailSettings] = useState(initialEmailSettings);
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(false);
  const [emailSettingsSaving, setEmailSettingsSaving] = useState(false);
  const [smtpDiagnostic, setSmtpDiagnostic] = useState(null);
  const [smtpJournal, setSmtpJournal] = useState([]);
  const [dadataSettings, setDadataSettings] = useState(initialDadataSettings);
  const [dadataSettingsSaving, setDadataSettingsSaving] = useState(false);
  const [telegramSettingsSaving, setTelegramSettingsSaving] = useState(false);
  const [telegramTesting, setTelegramTesting] = useState(false);
  const [telegramProxyTesting, setTelegramProxyTesting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await apiService.getUserProfile();
        if (!isMounted) {
          return;
        }
        const normalizedProfile = normalizeProfile(response.data);

        savedProfileRef.current = normalizedProfile;
        setProfile(normalizedProfile);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        console.error('Не удалось получить профиль пользователя', err);
        setError('Не удалось загрузить профиль. Попробуйте обновить страницу.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadEmailSettings = async () => {
      if (!isAdmin) {
        return;
      }

      setEmailSettingsLoading(true);
      try {
        const [resp, uiResp] = await Promise.all([
          apiService.getEmailSettings(),
          apiService.getUiSettings(),
        ]);
        if (!isMounted) {
          return;
        }
        setEmailSettings(normalizeEmailSettings(resp.data));
        setDadataSettings((prev) => ({
          ...prev,
          has_dadata_api_token: Boolean(uiResp?.data?.has_dadata_api_token),
          has_telegram_bot_token: Boolean(uiResp?.data?.has_telegram_bot_token),
          telegram_admin_chat_ids: String(uiResp?.data?.telegram_admin_chat_ids || ''),
          telegram_proxy_url: String(uiResp?.data?.telegram_proxy_url || ''),
          has_telegram_proxy_url: Boolean(uiResp?.data?.has_telegram_proxy_url),
        }));
      } catch (err) {
        if (!isMounted) {
          return;
        }
        console.error('Не удалось загрузить SMTP настройки', err);
      } finally {
        if (isMounted) {
          setEmailSettingsLoading(false);
        }
      }
    };

    loadEmailSettings();
    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  useEffect(() => {
    if (!toast) {
      return;
    }

    const timer = setTimeout(() => {
      setToast(null);
    }, 3000);

    return () => clearTimeout(timer);
  }, [toast]);

  const formatDate = useMemo(() => {
    if (!profile.registration_date) {
      return '—';
    }
    try {
      return new Intl.DateTimeFormat('ru-RU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(new Date(profile.registration_date));
    } catch (e) {
      return profile.registration_date;
    }
  }, [profile.registration_date]);

  const handlePhoneInput = (value) => {
    const digits = normalizePhoneDigits(value);
    const formatted = digits ? formatPhone(digits) : '';
    setProfile((prev) => ({
      ...prev,
      phone: formatted,
    }));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    if (name === 'phone') {
      handlePhoneInput(value);
      return;
    }
    if (name === 'date_of_birth') {
      setProfile((prev) => ({
        ...prev,
        date_of_birth: formatBirthDateInput(value),
      }));
      return;
    }

    setProfile((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      return next;
    });
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswords((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleReset = () => {
    setProfile({ ...savedProfileRef.current });
    setPasswords(initialPasswords);
    setFieldErrors({});
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSaving(true);
    setError('');
    setFieldErrors({});

    try {
      const timerValue = Number(profile.inactivity_timeout_minutes);
      if (!Number.isFinite(timerValue) || timerValue < 5 || timerValue > 720) {
        throw new Error('Таймаут бездействия можно установить от 5 до 720 минут.');
      }

      const wantsPasswordChange = passwords.current || passwords.next || passwords.confirm;
      if (wantsPasswordChange) {
        if (!passwords.current || !passwords.next || !passwords.confirm) {
          throw new Error('Для смены пароля заполните текущий пароль, новый пароль и подтверждение.');
        }
        if (passwords.next !== passwords.confirm) {
          throw new Error('Новый пароль и подтверждение не совпадают.');
        }
      }

      const fullNameValue = String(profile.full_name || '').trim();
      const emailValue = String(profile.email || '').trim();
      const requiredErrors = {};
      if (!fullNameValue) {
        requiredErrors.full_name = 'Укажите ФИО.';
      }
      if (!emailValue) {
        requiredErrors.email = 'Укажите email.';
      }
      if (Object.keys(requiredErrors).length > 0) {
        setFieldErrors(requiredErrors);
        const firstKey = Object.keys(requiredErrors)[0];
        const firstLabel = PROFILE_FIELD_LABELS[firstKey] || firstKey;
        throw new Error(`Поле «${firstLabel}»: ${requiredErrors[firstKey]}`);
      }
      const dateOfBirthValue = (() => {
        if (!profile.date_of_birth) return null;
        const d = parseBirthDateStrict(profile.date_of_birth);
        if (!d) return null;
        const pad = (v) => String(v).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      })();
      if (profile.date_of_birth && !dateOfBirthValue) {
        throw new Error('Дата рождения должна быть в формате ДД.ММ.ГГГГ и содержать корректную дату.');
      }

      const profilePayload = {
        full_name: fullNameValue,
        email: emailValue,
        phone: (profile.phone || '').trim(),
        gender: profile.gender,
        date_of_birth: dateOfBirthValue,
        inactivity_timeout_minutes: timerValue,
      };

      const requestPayload = { ...profilePayload };

      if (wantsPasswordChange) {
        requestPayload.current_password = passwords.current;
        requestPayload.new_password = passwords.next;
        requestPayload.new_password_confirm = passwords.confirm;
      }

      const response = await apiService.updateUserProfile(requestPayload);
      const updatedUserData = response.data?.user || {
        ...user,
        ...profilePayload,
      };
      const normalized = normalizeProfile(updatedUserData);

      savedProfileRef.current = normalized;
      setProfile(normalized);
      setPasswords(initialPasswords);
      updateUser(updatedUserData);

      setToast({
        type: 'success',
        message: response.data?.message || 'Профиль успешно обновлён.',
      });
    } catch (err) {
      console.error('Ошибка при обновлении профиля', err);
      const apiFieldErrors = mapApiFieldErrors(err.response?.data);
      if (Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors(apiFieldErrors);
      }
      const firstApiField = Object.keys(apiFieldErrors)[0];
      const firstApiFieldLabel = firstApiField ? (PROFILE_FIELD_LABELS[firstApiField] || firstApiField) : '';
      const message = extractErrorMessage(
        firstApiField
          ? `Поле «${firstApiFieldLabel}»: ${apiFieldErrors[firstApiField]}`
          : (
            err.response?.data?.error ||
            err.response?.data?.detail ||
            err.response?.data ||
            err.message
          ),
      );
      setToast({
        type: 'error',
        message,
      });
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setAdminEmailTesting(true);
    setError('');
    try {
      const target = (adminTestEmail || profile.email || '').trim();
      const resp = await apiService.testEmailSettings(target);
      const backend = resp?.data?.config?.email_backend ? ` (${resp.data.config.email_backend})` : '';
      const diagnostic = resp?.data?.diagnostic || null;
      const protocol = Array.isArray(resp?.data?.protocol) ? resp.data.protocol : [];
      setSmtpDiagnostic({
        at: new Date().toISOString(),
        ok: true,
        to_email: resp?.data?.to_email || target,
        config: resp?.data?.config || null,
        diagnostic,
        protocol,
      });
      setSmtpJournal((prev) => {
        const next = [
          {
            at: new Date().toISOString(),
            ok: true,
            to_email: resp?.data?.to_email || target,
            stage: diagnostic?.stage || 'send',
            backend: diagnostic?.backend || 'unknown',
            message: 'Успешно отправлено',
          },
          ...prev,
        ];
        return next.slice(0, 10);
      });
      setToast({
        type: 'success',
        message: `Тестовое письмо отправлено на ${resp.data.to_email}${backend}`,
      });
    } catch (err) {
      const apiError = err.response?.data;
      const errorText = extractErrorMessage(apiError?.error || apiError?.detail || apiError || err.message);
      const detailsText = extractErrorMessage(apiError?.details);
      const composed = detailsText && detailsText !== errorText ? `${errorText}. Причина: ${detailsText}` : errorText;
      const protocol = Array.isArray(apiError?.protocol) ? apiError.protocol : [];

      setSmtpDiagnostic({
        at: new Date().toISOString(),
        ok: false,
        to_email: (adminTestEmail || profile.email || '').trim(),
        config: apiError?.config || null,
        diagnostic: apiError?.diagnostic || null,
        protocol,
        error_type: apiError?.error_type || null,
        error: apiError?.error || null,
        details: apiError?.details || null,
      });
      setSmtpJournal((prev) => {
        const next = [
          {
            at: new Date().toISOString(),
            ok: false,
            to_email: (adminTestEmail || profile.email || '').trim(),
            stage: apiError?.diagnostic?.stage || 'unknown',
            backend: apiError?.diagnostic?.backend || 'unknown',
            message: composed,
          },
          ...prev,
        ];
        return next.slice(0, 10);
      });
      setToast({
        type: 'error',
        message: composed,
      });
      setError(composed);
    } finally {
      setAdminEmailTesting(false);
    }
  };

  const handleEmailSettingsChange = (event) => {
    const { name, value, type, checked } = event.target;
    setEmailSettings((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      if (name === 'use_tls' && checked) {
        next.use_ssl = false;
      }
      if (name === 'use_ssl' && checked) {
        next.use_tls = false;
      }

      return next;
    });
  };

  const handleSaveEmailSettings = async () => {
    setEmailSettingsSaving(true);
    setError('');
    try {
      const port = Number(emailSettings.port);
      if (!Number.isFinite(port) || port <= 0 || port > 65535) {
        throw new Error('Укажите корректный порт (1–65535).');
      }

      await apiService.saveEmailSettings({
        frontend_url: (emailSettings.frontend_url || '').trim(),
        host: (emailSettings.host || '').trim(),
        port,
        host_user: (emailSettings.host_user || '').trim(),
        host_password: emailSettings.host_password,
        use_tls: Boolean(emailSettings.use_tls),
        use_ssl: Boolean(emailSettings.use_ssl),
        default_from_email: (emailSettings.default_from_email || '').trim(),
      });

      const refreshed = await apiService.getEmailSettings();
      setEmailSettings(normalizeEmailSettings(refreshed.data));

      setToast({
        type: 'success',
        message: 'Настройки SMTP сохранены.',
      });
    } catch (err) {
      console.error('Ошибка при сохранении SMTP настроек', err);
      const message = extractErrorMessage(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          err.response?.data ||
          err.message,
      );
      setToast({
        type: 'error',
        message,
      });
      setError(message);
    } finally {
      setEmailSettingsSaving(false);
    }
  };

  const handleSaveDadataSettings = async () => {
    setDadataSettingsSaving(true);
    setError('');
    try {
      await apiService.saveUiSettings({
        dadata_api_token: (dadataSettings.dadata_api_token || '').trim(),
      });
      const refreshed = await apiService.getUiSettings();
      setDadataSettings({
        dadata_api_token: '',
        has_dadata_api_token: Boolean(refreshed?.data?.has_dadata_api_token),
      });
      setToast({
        type: 'success',
        message: 'Настройки DaData сохранены.',
      });
    } catch (err) {
      console.error('Ошибка при сохранении настроек DaData', err);
      const message = extractErrorMessage(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          err.response?.data ||
          err.message,
      );
      setToast({
        type: 'error',
        message,
      });
      setError(message);
    } finally {
      setDadataSettingsSaving(false);
    }
  };

  const handleSaveTelegramSettings = async () => {
    setTelegramSettingsSaving(true);
    setError('');
    try {
      await apiService.saveUiSettings({
        telegram_bot_token: (dadataSettings.telegram_bot_token || '').trim(),
        telegram_admin_chat_ids: (dadataSettings.telegram_admin_chat_ids || '').trim(),
        telegram_proxy_url: (dadataSettings.telegram_proxy_url || '').trim(),
      });
      const refreshed = await apiService.getUiSettings();
      setDadataSettings((prev) => ({
        ...prev,
        telegram_bot_token: '',
        has_telegram_bot_token: Boolean(refreshed?.data?.has_telegram_bot_token),
        telegram_admin_chat_ids: String(refreshed?.data?.telegram_admin_chat_ids || ''),
        telegram_proxy_url: String(refreshed?.data?.telegram_proxy_url || ''),
        has_telegram_proxy_url: Boolean(refreshed?.data?.has_telegram_proxy_url),
      }));
      setToast({
        type: 'success',
        message: 'Настройки Telegram сохранены.',
      });
    } catch (err) {
      console.error('Ошибка при сохранении настроек Telegram', err);
      const message = extractErrorMessage(
        err.response?.data?.error ||
          err.response?.data?.detail ||
          err.response?.data ||
          err.message,
      );
      setToast({
        type: 'error',
        message,
      });
      setError(message);
    } finally {
      setTelegramSettingsSaving(false);
    }
  };

  const handleTestTelegramSettings = async () => {
    setTelegramTesting(true);
    setError('');
    try {
      const resp = await apiService.testTelegramSettings();
      const total = Number(resp?.data?.recipients_total || 0);
      const sent = Number(resp?.data?.sent_count || 0);
      setToast({
        type: 'success',
        message: `Тест Telegram отправлен: ${sent} из ${total}.`,
      });
    } catch (err) {
      const message = extractErrorMessage(
        err.response?.data?.error ||
          err.response?.data?.details ||
          err.response?.data?.detail ||
          err.response?.data ||
          err.message,
      );
      setToast({
        type: 'error',
        message,
      });
      setError(message);
    } finally {
      setTelegramTesting(false);
    }
  };

  const handleTestTelegramProxy = async () => {
    setTelegramProxyTesting(true);
    setError('');
    try {
      const resp = await apiService.testTelegramProxy();
      const code = resp?.data?.status_code;
      setToast({
        type: 'success',
        message: `Прокси доступен. Ответ Telegram API: ${code || 'OK'}.`,
      });
    } catch (err) {
      const data = err?.response?.data || {};
      const message = extractErrorMessage(
        data?.error ||
          data?.details ||
          data?.detail ||
          data ||
          err.message,
      );
      setToast({
        type: 'error',
        message,
      });
      setError(message);
    } finally {
      setTelegramProxyTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-card">
          <p>Загрузка профиля...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <Toast toast={toast} />
      <div className="profile-card">
        <h1 className="profile-title">Профиль пользователя</h1>

        <form className="profile-form" onSubmit={handleSubmit}>
          <div className="profile-section">
            <h2 className="profile-section-title">Основная информация</h2>
            <div className="profile-fields">
              <div className="profile-row">
                <span className="profile-row-label">ФИО *</span>
                <div className="profile-row-control">
                  <input
                    type="text"
                    name="full_name"
                    value={profile.full_name || ''}
                    onChange={handleChange}
                    placeholder="Введите ФИО"
                    className={fieldErrors.full_name ? 'profile-input-error' : ''}
                  />
                </div>
              </div>
              <div className="profile-row">
                <span className="profile-row-label">Email *</span>
                <div className="profile-row-control">
                  <input
                    type="email"
                    name="email"
                    value={profile.email}
                    onChange={handleChange}
                    placeholder="Введите email"
                    className={fieldErrors.email ? 'profile-input-error' : ''}
                  />
                </div>
              </div>
              <div className="profile-row">
                <span className="profile-row-label">Телефон</span>
                <div className="profile-row-control">
                  <input
                    type="tel"
                    name="phone"
                    value={profile.phone || ''}
                    onChange={handleChange}
                    placeholder="+7 (___) ___-__-__"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h2 className="profile-section-title">Персональные данные</h2>
            <div className="profile-fields">
              <div className="profile-row">
                <span className="profile-row-label">Пол</span>
                <div className="profile-row-control">
                  <select
                    name="gender"
                    value={profile.gender}
                    onChange={handleChange}
                  >
                    {genderOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="profile-row">
                <span className="profile-row-label">Дата рождения</span>
                <div className="profile-row-control">
                  <input
                    type="text"
                    name="date_of_birth"
                    value={profile.date_of_birth}
                    onChange={handleChange}
                    placeholder="ДД.ММ.ГГГГ"
                    inputMode="numeric"
                    maxLength={10}
                  />
                </div>
              </div>
              <div className="profile-row">
                <span className="profile-row-label">Таймаут бездействия (минуты)</span>
                <div className="profile-row-control profile-row-control--with-hint">
                  <input
                    type="number"
                    min="5"
                    max="720"
                    step="5"
                    name="inactivity_timeout_minutes"
                    value={profile.inactivity_timeout_minutes}
                    onChange={handleChange}
                    placeholder="30"
                  />
                  <span className="profile-hint">Минимум 5 минут, максимум 12 часов.</span>
                </div>
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h2 className="profile-section-title">Системные сведения</h2>
            <div className="profile-fields">
              <div className="profile-row">
                <span className="profile-row-label">Роль</span>
                <div className="profile-row-value">
                  {profile.role ? ROLE_LABELS[profile.role] || profile.role : 'Не указано'}
                </div>
              </div>
              <div className="profile-row">
                <span className="profile-row-label">Дата регистрации</span>
                <div className="profile-row-value">{formatDate}</div>
              </div>
              <div className="profile-row">
                <span className="profile-row-label">Полное имя</span>
                <div className="profile-row-value">
                  {profile.full_name || 'Не указано'}
                </div>
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h2 className="profile-section-title">Безопасность</h2>
            <div className="profile-fields">
              <div className="profile-row">
                <span className="profile-row-label">Текущий пароль</span>
                <div className="profile-row-control">
                  <input
                    type="password"
                    name="current"
                    value={passwords.current}
                    onChange={handlePasswordChange}
                    placeholder="Введите текущий пароль"
                  />
                </div>
              </div>
              <div className="profile-row">
                <span className="profile-row-label">Новый пароль</span>
                <div className="profile-row-control">
                  <input
                    type="password"
                    name="next"
                    value={passwords.next}
                    onChange={handlePasswordChange}
                    placeholder="Введите новый пароль"
                  />
                </div>
              </div>
              <div className="profile-row">
                <span className="profile-row-label">Повторите новый пароль</span>
                <div className="profile-row-control">
                  <input
                    type="password"
                    name="confirm"
                    value={passwords.confirm}
                    onChange={handlePasswordChange}
                    placeholder="Повторите новый пароль"
                  />
                </div>
              </div>
            </div>
            <p className="profile-hint">
              Чтобы изменить пароль, заполните все три поля. Минимальная длина нового пароля — 8 символов.
            </p>
          </div>

          {error && <div className="profile-error">{error}</div>}

          <div className="profile-actions">
            <button
              type="button"
              className="profile-button profile-button--secondary"
              onClick={handleReset}
              disabled={saving}
            >
              Сбросить
            </button>
            <button
              type="submit"
              className="profile-button profile-button--primary"
              disabled={saving}
            >
              {saving ? 'Сохранение…' : 'Сохранить изменения'}
            </button>
          </div>
        </form>

        {!!isAdmin && (
          <>
            <div className="admin-tools">
              <h3>Админ: DaData</h3>
              <div className="admin-tools-block">
              <div className="admin-tools-grid">
                <label htmlFor="dadata-token">API ключ DaData</label>
                <div className="admin-tools-password">
                  <input
                    id="dadata-token"
                    type="password"
                    value={dadataSettings.dadata_api_token}
                    onChange={(e) =>
                      setDadataSettings((prev) => ({
                        ...prev,
                        dadata_api_token: e.target.value,
                      }))
                    }
                    placeholder={dadataSettings.has_dadata_api_token ? 'Не изменять (уже задан)' : 'Введите API ключ'}
                    autoComplete="new-password"
                  />
                  <div className="admin-tools-muted">
                    {dadataSettings.has_dadata_api_token
                      ? 'Ключ сохранён. Чтобы изменить — введите новый.'
                      : 'Ключ ещё не задан.'}
                  </div>
                </div>
                <div />
                <button
                  type="button"
                  className="profile-button profile-button--primary"
                  onClick={handleSaveDadataSettings}
                  disabled={dadataSettingsSaving}
                  style={{ padding: '10px 14px', justifySelf: 'start' }}
                >
                  {dadataSettingsSaving ? 'Сохранение…' : 'Сохранить DaData'}
                </button>
              </div>
            </div>
            <div className="admin-tools-block" style={{ marginTop: 12 }}>
              <div className="admin-tools-grid">
                <label htmlFor="telegram-bot-token">Telegram Bot Token</label>
                <div className="admin-tools-password">
                  <input
                    id="telegram-bot-token"
                    type="password"
                    value={dadataSettings.telegram_bot_token}
                    onChange={(e) =>
                      setDadataSettings((prev) => ({
                        ...prev,
                        telegram_bot_token: e.target.value,
                      }))
                    }
                    placeholder={dadataSettings.has_telegram_bot_token ? 'Не изменять (уже задан)' : 'Введите токен бота'}
                    autoComplete="new-password"
                  />
                  <div className="admin-tools-muted">
                    {dadataSettings.has_telegram_bot_token
                      ? 'Токен сохранён. Чтобы изменить — введите новый.'
                      : 'Токен ещё не задан.'}
                  </div>
                </div>

                <label htmlFor="telegram-chat-ids">Chat ID администраторов</label>
                <div className="admin-tools-password">
                  <input
                    id="telegram-chat-ids"
                    type="text"
                    value={dadataSettings.telegram_admin_chat_ids}
                    onChange={(e) =>
                      setDadataSettings((prev) => ({
                        ...prev,
                        telegram_admin_chat_ids: e.target.value,
                      }))
                    }
                    placeholder="123456789,-100987654321"
                  />
                  <div className="admin-tools-muted">
                    Можно несколько chat_id через запятую.
                  </div>
                </div>

                <label htmlFor="telegram-proxy-url">Proxy URL для Telegram</label>
                <div className="admin-tools-password">
                  <input
                    id="telegram-proxy-url"
                    type="text"
                    value={dadataSettings.telegram_proxy_url}
                    onChange={(e) =>
                      setDadataSettings((prev) => ({
                        ...prev,
                        telegram_proxy_url: e.target.value,
                      }))
                    }
                    placeholder={dadataSettings.has_telegram_proxy_url
                      ? 'Не изменять (proxy уже задан)'
                      : 'http://user:pass@host:port или socks5h://host:port'}
                  />
                  <div className="admin-tools-muted">
                    Используется только для отправки уведомлений в Telegram.
                  </div>
                </div>

                <div />
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="profile-button profile-button--primary"
                    onClick={handleSaveTelegramSettings}
                    disabled={telegramSettingsSaving}
                    style={{ padding: '10px 14px' }}
                  >
                    {telegramSettingsSaving ? 'Сохранение…' : 'Сохранить Telegram'}
                  </button>
                  <button
                    type="button"
                    className="profile-button profile-button--secondary"
                    onClick={handleTestTelegramSettings}
                    disabled={telegramTesting || telegramProxyTesting || telegramSettingsSaving}
                    style={{ padding: '10px 14px' }}
                  >
                    {telegramTesting ? 'Отправка…' : 'Тест Telegram'}
                  </button>
                  <button
                    type="button"
                    className="profile-button profile-button--secondary"
                    onClick={handleTestTelegramProxy}
                    disabled={telegramProxyTesting || telegramTesting || telegramSettingsSaving}
                    style={{ padding: '10px 14px' }}
                  >
                    {telegramProxyTesting ? 'Проверка…' : 'Проверить прокси'}
                  </button>
                </div>
              </div>
            </div>
            </div>

            <div className="admin-tools">
            <h3>Админ: SMTP</h3>
            <div className="admin-tools-block">
              <h4>Настройки SMTP</h4>

              {emailSettingsLoading ? (
                <div className="admin-tools-muted">Загрузка настроек…</div>
              ) : (
                <div className="admin-tools-grid">
                  <label htmlFor="smtp-frontend-url">URL фронтенда</label>
                  <input
                    id="smtp-frontend-url"
                    type="text"
                    name="frontend_url"
                    value={emailSettings.frontend_url}
                    onChange={handleEmailSettingsChange}
                    placeholder="https://example.com"
                  />

                  <label htmlFor="smtp-host">SMTP сервер</label>
                  <input
                    id="smtp-host"
                    type="text"
                    name="host"
                    value={emailSettings.host}
                    onChange={handleEmailSettingsChange}
                    placeholder="mail.example.com"
                  />

                  <label htmlFor="smtp-port">Порт</label>
                  <input
                    id="smtp-port"
                    type="number"
                    name="port"
                    value={emailSettings.port}
                    onChange={handleEmailSettingsChange}
                    placeholder="25"
                    min="1"
                    max="65535"
                  />

                  <label htmlFor="smtp-user">Логин</label>
                  <input
                    id="smtp-user"
                    type="text"
                    name="host_user"
                    value={emailSettings.host_user}
                    onChange={handleEmailSettingsChange}
                    placeholder="aot@example.com"
                  />

                  <label htmlFor="smtp-password">Пароль</label>
                  <div className="admin-tools-password">
                    <input
                      id="smtp-password"
                      type="password"
                      name="host_password"
                      value={emailSettings.host_password}
                      onChange={handleEmailSettingsChange}
                      placeholder={emailSettings.has_password ? 'Не изменять (уже задан)' : 'Введите пароль'}
                      autoComplete="new-password"
                    />
                    <div className="admin-tools-muted">
                      {emailSettings.has_password ? 'Пароль сохранён. Чтобы изменить — введите новый.' : 'Пароль ещё не задан.'}
                    </div>
                  </div>

                  <label>Шифрование</label>
                  <div className="admin-tools-checkbox-row">
                    <label className="admin-tools-checkbox">
                      <input
                        type="checkbox"
                        name="use_tls"
                        checked={emailSettings.use_tls}
                        onChange={handleEmailSettingsChange}
                      />
                      <span>TLS</span>
                    </label>
                    <label className="admin-tools-checkbox">
                      <input
                        type="checkbox"
                        name="use_ssl"
                        checked={emailSettings.use_ssl}
                        onChange={handleEmailSettingsChange}
                      />
                      <span>SSL</span>
                    </label>
                  </div>

                  <label htmlFor="smtp-from">От кого (From)</label>
                  <input
                    id="smtp-from"
                    type="text"
                    name="default_from_email"
                    value={emailSettings.default_from_email}
                    onChange={handleEmailSettingsChange}
                    placeholder="aot@example.com"
                  />

                  <div />
                  <button
                    type="button"
                    className="profile-button profile-button--primary"
                    onClick={handleSaveEmailSettings}
                    disabled={emailSettingsSaving}
                    style={{ padding: '10px 14px', justifySelf: 'start' }}
                  >
                    {emailSettingsSaving ? 'Сохранение…' : 'Сохранить SMTP'}
                  </button>
                </div>
              )}
            </div>

            <div className="admin-tools-block">
              <h4>Проверка SMTP</h4>
              <div className="admin-tools-row">
                <input
                  type="email"
                  value={adminTestEmail}
                  onChange={(e) => setAdminTestEmail(e.target.value)}
                  placeholder="Email для тестового письма (по умолчанию — ваш)"
                />
                <button
                  type="button"
                  className="profile-button profile-button--primary"
                  onClick={handleTestEmail}
                  disabled={adminEmailTesting}
                  style={{ padding: '10px 14px' }}
                >
                  {adminEmailTesting ? 'Отправка…' : 'Отправить'}
                </button>
              </div>

              {!!smtpDiagnostic && (
                <div className="admin-tools-diagnostic">
                  <div className="admin-tools-diagnostic-title">Диагностика</div>
                  <pre className="admin-tools-diagnostic-pre">
{JSON.stringify(
  {
    at: smtpDiagnostic.at,
    ok: smtpDiagnostic.ok,
    to_email: smtpDiagnostic.to_email,
    stage: smtpDiagnostic.diagnostic?.stage,
    backend: smtpDiagnostic.diagnostic?.backend,
    error_type: smtpDiagnostic.error_type,
    error: smtpDiagnostic.error,
    details: smtpDiagnostic.details,
    protocol_len: Array.isArray(smtpDiagnostic.protocol) ? smtpDiagnostic.protocol.length : 0,
    config: smtpDiagnostic.config,
  },
  null,
  2,
)}
                  </pre>

                  <div className="admin-tools-diagnostic-title" style={{ marginTop: 12 }}>
                    SMTP протокол (команды/ответы)
                  </div>
                  {Array.isArray(smtpDiagnostic.protocol) && smtpDiagnostic.protocol.length ? (
                    <pre className="admin-tools-diagnostic-pre">
{smtpDiagnostic.protocol
  .map((row) => `${row.dir === 'C' ? 'C' : 'S'}: ${row.line}`)
  .join('\n')}
                    </pre>
                  ) : (
                    <div className="admin-tools-muted">
                      Протокол пуст. Обычно так бывает, если соединение с сервером не установилось (таймаут/порт/доступ).
                    </div>
                  )}
                </div>
              )}

              {!!smtpJournal.length && (
                <div className="admin-tools-diagnostic">
                  <div className="admin-tools-diagnostic-title">Журнал попыток (последние 10)</div>
                  <div className="admin-tools-journal">
                    {smtpJournal.map((row, idx) => (
                      <div
                        key={`${row.at}-${idx}`}
                        className={`admin-tools-journal-row ${row.ok ? 'admin-tools-journal-row--ok' : 'admin-tools-journal-row--fail'}`}
                      >
                        <div className="admin-tools-journal-meta">
                          <span className="admin-tools-journal-dot" />
                          <span className="admin-tools-journal-time">
                            {new Date(row.at).toLocaleString('ru-RU')}
                          </span>
                          <span className="admin-tools-journal-stage">
                            {row.backend}/{row.stage}
                          </span>
                          <span className="admin-tools-journal-email">{row.to_email || '—'}</span>
                        </div>
                        <div className="admin-tools-journal-message">{row.message}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
